/**
 * AWS service icons for the DataFlowGraph (CR4).
 *
 * Inline SVG "resource tile" icons (a brand-colored rounded square with a white
 * glyph) mirroring the AWS Architecture Icons deck, packaged as React
 * components so the graph's compute / egress / ingress nodes show the AWS
 * service they represent instead of a bare colored box.
 *
 * Each icon exposes a stable `aws-svc-icon aws-svc-icon--<key>` class name and a
 * `data-service` attribute so Playwright UI tests can assert icon presence, and
 * a `<title>` / `aria-label` for accessibility.
 */
import React from 'react';

export type AwsServiceKey =
  | 'LAMBDA'
  | 'BEDROCK'
  | 'SQS'
  | 'SNS'
  | 'EVENTBRIDGE'
  | 'EVENTSTORE'
  | 'FORMULA'
  | 'GENERIC';

interface ServiceMeta {
  color: string;
  label: string;
}

const SERVICE_META: Record<AwsServiceKey, ServiceMeta> = {
  LAMBDA: { color: '#ED7100', label: 'AWS Lambda' },
  BEDROCK: { color: '#01A88D', label: 'Amazon Bedrock' },
  SQS: { color: '#E7157B', label: 'Amazon SQS' },
  SNS: { color: '#CD2264', label: 'Amazon SNS' },
  EVENTBRIDGE: { color: '#B0084D', label: 'Amazon EventBridge' },
  EVENTSTORE: { color: '#EC7211', label: 'EventStore' },
  FORMULA: { color: '#B8860B', label: 'Formula Rule' },
  GENERIC: { color: '#7D8998', label: 'AWS resource' },
};

/**
 * Normalize a rule target type / ingress source type to a known icon key.
 * Unknown types fall back to GENERIC so the graph never renders a broken icon.
 */
export function serviceKeyFromType(type: string | undefined | null): AwsServiceKey {
  const t = (type || '').toUpperCase();
  if (t === 'LAMBDA') return 'LAMBDA';
  if (t === 'BEDROCK') return 'BEDROCK';
  if (t === 'SQS') return 'SQS';
  if (t === 'SNS') return 'SNS';
  if (t === 'EVENTBRIDGE' || t === 'EVENTBUS' || t === 'EB') return 'EVENTBRIDGE';
  if (t === 'EVENTSTORE' || t === 'ES' || t === 'INTERNAL') return 'EVENTSTORE';
  if (t === 'FORMULA' || t === 'THRESHOLD' || t === 'ACCUMULATE') return 'FORMULA';
  return 'GENERIC';
}

/** White glyph drawn on top of the colored tile, one per service. */
function glyph(service: AwsServiceKey): React.ReactNode {
  switch (service) {
    case 'LAMBDA':
      // λ wedge
      return <path d="M7 18 L11.4 6 h1.9 L18 18 h-2.2 L12.3 8.9 L9.2 18 Z" fill="#fff" />;
    case 'BEDROCK':
      // node graph / "brain" of connected dots
      return (
        <g fill="none" stroke="#fff" strokeWidth="1.1">
          <path d="M7 8 L17 7 M17 7 L13 16 M7 8 L13 16" />
          <circle cx="7" cy="8" r="1.7" fill="#fff" stroke="none" />
          <circle cx="17" cy="7" r="1.7" fill="#fff" stroke="none" />
          <circle cx="13" cy="16" r="1.7" fill="#fff" stroke="none" />
        </g>
      );
    case 'SQS':
      // stacked queue messages
      return (
        <g fill="#fff">
          <rect x="5.5" y="7" width="9" height="5" rx="1.2" />
          <rect x="9.5" y="12" width="9" height="5" rx="1.2" fillOpacity="0.85" />
        </g>
      );
    case 'SNS':
      // broadcast / fan-out triangle
      return (
        <g fill="#fff">
          <path d="M12 5 L18.5 18 H5.5 Z" />
          <circle cx="12" cy="14.5" r="1.4" fill="#CD2264" />
        </g>
      );
    case 'EVENTBRIDGE':
      // event bus chevrons
      return (
        <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 6 L13 12 L7 18" />
          <path d="M12 6 L18 12 L12 18" />
        </g>
      );
    case 'EVENTSTORE':
      // database cylinder
      return (
        <g fill="#fff">
          <ellipse cx="12" cy="7" rx="6" ry="2.3" />
          <path d="M6 7 v10 c0 1.3 2.7 2.3 6 2.3 s6-1 6-2.3 V7 c0 1.3 -2.7 2.3 -6 2.3 s-6 -1 -6 -2.3 Z" fillOpacity="0.9" />
        </g>
      );
    case 'FORMULA':
      // Sigma Σ (accumulator/threshold)
      return (
        <g fill="#fff">
          <path d="M7 6 H17 L11.5 12 L17 18 H7 L12.5 12 Z" fillRule="evenodd" />
        </g>
      );
    case 'GENERIC':
    default:
      return <circle cx="12" cy="12" r="5" fill="#fff" />;
  }
}

export interface ServiceIconProps {
  service: AwsServiceKey;
  size?: number;
}

/** A single inline AWS service resource-tile icon. */
export function ServiceIcon({ service, size = 14 }: ServiceIconProps): JSX.Element {
  const meta = SERVICE_META[service] || SERVICE_META.GENERIC;
  return (
    <svg
      role="img"
      aria-label={meta.label}
      data-testid="aws-svc-icon"
      data-service={service}
      className={`aws-svc-icon aws-svc-icon--${service.toLowerCase()}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ flexShrink: 0, display: 'block' }}
    >
      <title>{meta.label}</title>
      <rect x="0" y="0" width="24" height="24" rx="5" fill={meta.color} />
      {glyph(service)}
    </svg>
  );
}

/**
 * Build a React Flow node label: an AWS service icon followed by text. React
 * Flow default nodes accept a ReactNode as `data.label`, so this keeps the
 * existing dagre layout / sizing / centerOnNode logic untouched.
 */
export function serviceNodeLabel(
  service: AwsServiceKey,
  text: string,
  opts?: { prefix?: string; iconSize?: number }
): React.ReactNode {
  return (
    <span
      className="es-svc-label"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0 }}
    >
      {opts?.prefix ? <span style={{ flexShrink: 0 }}>{opts.prefix}</span> : null}
      <ServiceIcon service={service} size={opts?.iconSize ?? 14} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
    </span>
  );
}
